import React, { useState } from 'react';
import {
    Box,
    Card,
    Typography,
    Grid,
    IconButton,
    Collapse,
    Tabs,
    Tab,
    Stack,
    Tooltip,
    Chip,
    Paper,
    Alert,
    Divider,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
    alpha
} from '@mui/material';
import {
    Edit as EditIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Lock as LockIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Download as DownloadIcon,
    InsertDriveFile as FileIcon,
    CheckCircle as CheckCircleIcon,
    PendingActions as PendingActionsIcon,
    Verified as VerifiedIcon,
    Science as ScienceIcon,
    Assignment as AssignmentIcon,
    Engineering as EngineeringIcon,
    Description as DescriptionIcon,
    Analytics as AnalyticsIcon,
    VerifiedUser as VerifiedUserIcon,
    FilterVintage as FilterVintageIcon,
    Biotech as BiotechIcon,
    Psychology as PsychologyIcon,
    Speed as SpeedIcon,
    Opacity as OpacityIcon,
    Palette as PaletteIcon,
    Thermostat as ThermostatIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { testTypes } from './constants';
import { calculateDeviation, getCalibrationStatus } from './utils';
import { supabase } from '../../../../../supabaseClient';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`test-tabpanel-${index}`}
            aria-labelledby={`test-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const viewFile = async (filePath, fileName, bucketName) => {
    try {
        const { data, error } = await supabase.storage
            .from(bucketName)
            .download(filePath);

        if (error) throw error;

        const blobUrl = URL.createObjectURL(data);
        const newWindow = window.open(blobUrl, '_blank');
        
        if (!newWindow) {
            downloadFile(filePath, fileName, bucketName);
        }
        
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
        // Handle view error silently
    }
};

const downloadFile = async (filePath, fileName, bucketName) => {
    try {
        const { data, error } = await supabase.storage
            .from(bucketName)
            .download(filePath);

        if (error) throw error;

        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (err) {
        // Handle download error silently
    }
};

export default function TestCard({ 
    test, 
    isExpanded, 
    onToggleExpanded, 
    onEdit, 
    onTrendAnalysis,
    onQAReview,
    onReview,
    QAReviewForm,
    batchNumber,
    productInfo,
    onDeleteClick,
    canEdit = false,
    canDelete = false,
    canApprove = false,
    batchTimeFormat = '12hour'
}) {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(0);
    const isLocked = test.locked_at !== null;
    const calibStatus = getCalibrationStatus(test.calibration_due_date);
    const deviation = calculateDeviation(test.actual_result, test.target_value);
    const [showSignatureDialog, setShowSignatureDialog] = useState(false);
    
    return (
                <Card sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            backgroundColor: test.final_status === 'PASS' ? alpha(theme.palette.success.main, 0.05) : 
                           test.final_status === 'FAIL' ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.warning.main, 0.05),
            overflow: 'hidden',
            position: 'relative',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            {isLocked && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 12, 
                    left: 12, 
                    zIndex: 1 
                }}>
                    <Tooltip title="QA Approved - Locked">
                        <LockIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                    </Tooltip>
                </Box>
            )}
            
            {/* Header Section */}
            <Box 
                sx={{ 
                    p: 3, 
                    borderBottom: isExpanded ? `1px solid ${theme.palette.grey[400]}` : 'none',
                    backgroundColor: test.final_status === 'PASS' ? alpha(theme.palette.success.main, 0.1) : 
                                   test.final_status === 'FAIL' ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.warning.main, 0.1),
                    cursor: 'pointer'
                }}
                onClick={onToggleExpanded}
            >
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                            {testTypes.find(t => t.value === test.test_type)?.label || test.test_type}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {test.test_number} • {test.test_stage?.replace('_', ' ')}
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', fontWeight: 500 }}>
                                Result
                            </Typography>
                            <Typography variant="h4" sx={{ 
                                fontWeight: 500, 
                                color: test.final_status === 'PASS' ? theme.palette.success.main : 
                                       test.final_status === 'FAIL' ? theme.palette.error.main : theme.palette.warning.main,
                                lineHeight: 1.2
                            }}>
                                {test.result_text || test.final_status || '—'}
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'right' }}>
                            <Chip 
                                label={test.final_status}
                                size="large"
                                sx={{
                                    fontWeight: 500,
                                    fontSize: '1rem',
                                    backgroundColor: test.final_status === 'PASS' ? theme.palette.success.main : 
                                                   test.final_status === 'FAIL' ? theme.palette.error.main : theme.palette.warning.main,
                                    color: 'white',
                                    mb: 1
                                }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {test.is_critical_test && (
                                    <Chip 
                                        label="CRITICAL TEST"
                                        size="small"
                                        sx={{
                                            fontWeight: 500,
                                            backgroundColor: theme.palette.error.main,
                                            color: 'white',
                                            border: `1px solid ${theme.palette.error.dark}`
                                        }}
                                    />
                                )}
                                {test.qa_review_status === 'PENDING' && (
                                    <Chip 
                                        label="PENDING REVIEW"
                                        size="small"
                                        sx={{
                                            fontWeight: 500,
                                            backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                            color: theme.palette.warning.main,
                                            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                                        }}
                                    />
                                )}
                                {test.qa_review_status === 'APPROVED' && (
                                    <Chip 
                                        label="QA APPROVED"
                                        size="small"
                                        sx={{
                                            fontWeight: 500,
                                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                                            color: theme.palette.success.main,
                                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`
                                        }}
                                    />
                                )}
                            </Box>
                            <IconButton 
                                onClick={onToggleExpanded}
                                size="large"
                                sx={{ color: '#666', mt: 1 }}
                            >
                                {isExpanded ? <ExpandLessIcon fontSize="large" /> : <ExpandMoreIcon fontSize="large" />}
                            </IconButton>
                        </Box>
                    </Grid>
                </Grid>
                
                {/* Action Buttons */}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!isLocked && canEdit && onEdit && (
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(test);
                                }}
                                size="small"
                                sx={{ 
                                    fontWeight: 500,
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.grey[400]}`,
                                    color: theme.palette.text.secondary,
                                    '&:hover': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                    },
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                Edit
                            </Button>
                        )}
                        
                        {!isLocked && canDelete && onDeleteClick && (
                            <Button
                                variant="outlined"
                                startIcon={<DeleteIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteClick(test);
                                }}
                                size="small"
                                sx={{ 
                                    fontWeight: 500,
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.error.main}`,
                                    color: theme.palette.error.main,
                                    '&:hover': {
                                        border: `1px solid ${theme.palette.error.dark}`,
                                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`
                                    },
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                Delete
                            </Button>
                        )}
                        
                        {test.qa_review_status === 'PENDING' && canApprove && onReview && (
                            <Button
                                variant="contained"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onReview(test);
                                }}
                                size="small"
                                sx={{
                                    fontWeight: 500,
                                    borderRadius: 3,
                                    background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${alpha(theme.palette.warning.main, 0.8)})`,
                                    color: 'white',
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.9)}, ${alpha(theme.palette.warning.main, 0.7)})`,
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.25)}`
                                    },
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                Review
                            </Button>
                        )}
                    </Box>
                    
                    <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                        {dayjs(test.created_at).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                    </Typography>
                </Box>
            </Box>

            {/* Expanded Content with Tabs */}
            <Collapse in={isExpanded}>
                <Box sx={{ backgroundColor: 'white' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={(e, v) => setActiveTab(v)} 
                        sx={{ 
                            borderBottom: `1px solid ${theme.palette.grey[300]}`,
                            backgroundColor: alpha(theme.palette.grey[50], 0.8),
                            px: 2,
                            pt: 2,
                            '& .MuiTabs-flexContainer': {
                                gap: 1
                            },
                            '& .MuiTab-root': {
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                minWidth: 'auto',
                                minHeight: 56,
                                px: 3,
                                py: 1.5,
                                textTransform: 'none',
                                color: theme.palette.text.secondary,
                                backgroundColor: 'transparent',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    color: theme.palette.primary.main,
                                    transform: 'translateY(-2px)',
                                },
                                '&.Mui-selected': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`
                                    }
                                }
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#000000',
                                height: 3,
                                borderRadius: '2px 2px 0 0'
                            }
                        }}
                        variant="fullWidth"
                    >
                        <Tab 
                            icon={<EngineeringIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label="Test Setup" 
                        />
                        <Tab 
                            icon={<AnalyticsIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label="Specifications" 
                        />
                        <Tab 
                            icon={<ThermostatIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label="Environment" 
                        />
                        <Tab 
                            icon={<FilterVintageIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label="Sampling" 
                        />
                        <Tab 
                            icon={<VerifiedUserIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label="QA Review" 
                        />
                        <Tab 
                            icon={<DescriptionIcon sx={{ fontSize: 20 }} />}
                            iconPosition="start"
                            label="Attachments" 
                        />
                    </Tabs>
                    
                    <TabPanel value={activeTab} index={0}>
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ 
                                p: 2, 
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3, 
                                mb: 2,
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                }
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                                            Test Category
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.test_category?.replace('_', ' ') || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.secondary, textTransform: 'uppercase' }}>
                                            Test Stage
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.test_stage?.replace('_', ' ') || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Test Method
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.test_method || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Test Standard
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.test_standard || '—'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            
                            
                            <Box sx={{ 
                                p: 2, 
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3, 
                                mb: 2,
                                backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                }
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Product Name
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {productInfo?.name || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Batch Number
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {batchNumber || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Test Number
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.test_number || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Test Type
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.test_type || '—'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            
                            <Box sx={{ 
                                p: 2, 
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3,
                                backgroundColor: alpha(theme.palette.success.main, 0.05),
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.2)}`
                                }
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Critical Test
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.is_critical_test ? 'Yes' : 'No'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Test Datetime
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.test_datetime ? dayjs(test.test_datetime).format(batchTimeFormat === '24hour' ? 'MMM D, YYYY HH:mm' : 'MMM D, YYYY h:mm A') : '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Tested By
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.tested_by_name || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Tester Role
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.tested_by_role?.replace('_', ' ') || '—'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </TabPanel>
                    
                    <TabPanel value={activeTab} index={1}>
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ 
                                p: 2, 
                                border: '1px solid #9c27b0', 
                                borderRadius: 2, 
                                mb: 2,
                                backgroundColor: 'rgba(156, 39, 176, 0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: 'rgba(156, 39, 176, 0.1)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(156, 39, 176, 0.2)'
                                }
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Acceptance Criteria
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mt: 1 }}>
                                            {test.acceptance_criteria || '—'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            
                            <Box sx={{ 
                                p: 2, 
                                border: '1px solid #2196f3', 
                                borderRadius: 2, 
                                mb: 2,
                                backgroundColor: 'rgba(33, 150, 243, 0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.2)'
                                }
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Test Result
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.result_text || test.final_status || '—'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            
                            <Box sx={{ 
                                p: 2, 
                                border: '1px solid #795548', 
                                borderRadius: 2,
                                backgroundColor: 'rgba(121, 85, 72, 0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: 'rgba(121, 85, 72, 0.1)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(121, 85, 72, 0.2)'
                                }
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Calibration Status
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.calibration_status?.replace('_', ' ') || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Last Calibration Date
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.calibration_date ? dayjs(test.calibration_date).format('MMM D, YYYY') : '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Calibration Due Date
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.calibration_due_date ? dayjs(test.calibration_due_date).format('MMM D, YYYY') : '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Temperature Unit
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.temp_unit || '—'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </TabPanel>
                    
                    <TabPanel value={activeTab} index={2}>
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ 
                                p: 2, 
                                border: '1px solid #00bcd4', 
                                borderRadius: 2, 
                                mb: 2,
                                backgroundColor: 'rgba(0, 188, 212, 0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 188, 212, 0.1)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0, 188, 212, 0.2)'
                                }
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Room Temperature
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.room_temperature_c ? `${test.room_temperature_c}°C` : '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Room Humidity
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.room_humidity_percent ? `${test.room_humidity_percent}%` : '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Clean Room Classification
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.clean_room_classification || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Temperature Unit
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.temp_unit || '—'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            
                            <Box sx={{ 
                                p: 2, 
                                border: '1px solid #607d8b', 
                                borderRadius: 2, 
                                mb: 2,
                                backgroundColor: 'rgba(96, 125, 139, 0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: 'rgba(96, 125, 139, 0.1)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(96, 125, 139, 0.2)'
                                }
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Equipment ID
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.equipment_id || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Equipment Name
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.equipment_name || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Equipment Model
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.equipment_model || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Equipment Serial
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.equipment_serial || '—'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            

                        </Box>
                    </TabPanel>
                    
                    <TabPanel value={activeTab} index={3}>
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ 
                                p: 2, 
                                border: '1px solid #e91e63', 
                                borderRadius: 2, 
                                mb: 2,
                                backgroundColor: 'rgba(233, 30, 99, 0.05)',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: 'rgba(233, 30, 99, 0.1)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(233, 30, 99, 0.2)'
                                }
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Sample ID
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.sample_id || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Sample Location
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.sample_location || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Sample Quantity
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.sample_quantity ? `${test.sample_quantity} ${test.sample_unit || ''}` : '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Sample Unit
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.sample_unit || '—'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} md={6}>
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                            Sample Retention Location
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.sample_retention_location || '—'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            

                            
                            {test.notes && (
                                <Box sx={{ 
                                    p: 2, 
                                    border: '1px solid #ffc107', 
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(255, 193, 7, 0.05)',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(255, 193, 7, 0.2)'
                                    }
                                }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                                Sampling Notes & Observations
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                                                {test.notes}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    </TabPanel>
                    
                    <TabPanel value={activeTab} index={4}>
                        <Box sx={{ p: 2 }}>
                            {test.qa_reviewed_by_name ? (
                                <Box>
                                    {/* Approval Status Header */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between',
                                        mb: 3,
                                        p: 2,
                                        borderRadius: 3,
                                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                                        border: `1px solid ${theme.palette.success.main}`,
                                        backdropFilter: 'blur(10px)'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 32 }} />
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                                                    QA APPROVED & SIGNED
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    This test has been reviewed and approved
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {test.locked_at && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LockIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                                                    LOCKED
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* QA Signature - Maps based on role */}
                                    {test.qa_reviewed_by_name && (
                                        <Box sx={{ 
                                            p: 3, 
                                            border: `1px solid ${
                                                test.qa_reviewed_by_role?.toLowerCase().includes('supervisor') ? '#4caf50' :
                                                test.qa_reviewed_by_role?.toLowerCase().includes('manager') ? '#673ab7' : '#2196f3'
                                            }`, 
                                            borderRadius: 2, 
                                            mb: 2,
                                            backgroundColor: `${
                                                test.qa_reviewed_by_role?.toLowerCase().includes('supervisor') ? 'rgba(76, 175, 80, 0.05)' :
                                                test.qa_reviewed_by_role?.toLowerCase().includes('manager') ? 'rgba(103, 58, 183, 0.05)' : 'rgba(33, 150, 243, 0.05)'
                                            }`,
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: `${
                                                    test.qa_reviewed_by_role?.toLowerCase().includes('supervisor') ? 'rgba(76, 175, 80, 0.1)' :
                                                    test.qa_reviewed_by_role?.toLowerCase().includes('manager') ? 'rgba(103, 58, 183, 0.1)' : 'rgba(33, 150, 243, 0.1)'
                                                }`,
                                                transform: 'translateY(-2px)',
                                                boxShadow: `${
                                                    test.qa_reviewed_by_role?.toLowerCase().includes('supervisor') ? '0 4px 12px rgba(76, 175, 80, 0.2)' :
                                                    test.qa_reviewed_by_role?.toLowerCase().includes('manager') ? '0 4px 12px rgba(103, 58, 183, 0.2)' : '0 4px 12px rgba(33, 150, 243, 0.2)'
                                                }`
                                            }
                                        }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontWeight: 500, textTransform: 'uppercase' }}>
                                                {test.qa_reviewed_by_role?.toLowerCase().includes('supervisor') ? 'SUPERVISOR SIGNATURE:' :
                                                 test.qa_reviewed_by_role?.toLowerCase().includes('manager') ? 'QA MANAGER SIGNATURE:' : 'QA REVIEWER SIGNATURE:'}
                                            </Typography>
                                            <Box sx={{ 
                                                fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                                fontSize: '28px', 
                                                color: '#000000',
                                                textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                                mb: 2,
                                                p: 3,
                                                border: '1px solid #000000',
                                                borderRadius: 2,
                                                backgroundColor: '#f8fafc',
                                                textAlign: 'center'
                                            }}>
                                                {test.qa_reviewed_by_name}
                                            </Box>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                                        Role/Position
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {test.qa_reviewed_by_role?.replace('_', ' ') || 'QA Reviewer'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                                        {test.qa_reviewed_by_role?.toLowerCase().includes('supervisor') || test.qa_reviewed_by_role?.toLowerCase().includes('manager') ? 'Approval Date' : 'Review Date'}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {test.qa_review_datetime ? dayjs(test.qa_review_datetime).format(batchTimeFormat === '24hour' ? 'MMM D, YYYY HH:mm' : 'MMM D, YYYY h:mm A') : 'Pending'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Review Comments */}
                                    {test.qa_review_comments && (
                                        <Box sx={{ 
                                            p: 3, 
                                            border: '1px solid #009688', 
                                            borderRadius: 2,
                                            backgroundColor: 'rgba(0, 150, 136, 0.05)',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 150, 136, 0.1)',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 4px 12px rgba(0, 150, 136, 0.2)'
                                            }
                                        }}>
                                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#666', textTransform: 'uppercase' }}>
                                                QA Review Comments
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 1 }}>
                                                {test.qa_review_comments}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    textAlign: 'center', 
                                    p: 4,
                                    border: `1px dashed ${theme.palette.warning.main}`,
                                    borderRadius: 3,
                                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    <PendingActionsIcon sx={{ fontSize: 48, color: theme.palette.warning.main, mb: 2 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.warning.main, mb: 1 }}>
                                        QA Review Pending
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        This test is waiting for QA review and approval
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </TabPanel>
                    
                    <TabPanel value={activeTab} index={5}>
                        <Box sx={{ p: 2 }}>
                            {(test.photo_urls?.length > 0 || test.document_urls?.length > 0) && (
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                                        Attachments
                                    </Typography>
                                    
                                    <Grid container spacing={2}>
                                        {/* Photo Attachments */}
                                        {test.photo_urls?.length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.secondary }}>
                                                    Photo Attachments ({test.photo_urls.length})
                                                </Typography>
                                                {test.photo_urls.map((photoUrl, index) => (
                                                    <Box 
                                                        key={index}
                                                        sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'space-between',
                                                            p: 1,
                                                            mb: 0.5,
                                                            border: `1px solid ${theme.palette.grey[300]}`,
                                                            borderRadius: 2,
                                                            backgroundColor: alpha(theme.palette.grey[100], 0.5),
                                                            backdropFilter: 'blur(10px)'
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <FileIcon sx={{ color: '#666', fontSize: 16 }} />
                                                            <Typography variant="caption" sx={{ fontWeight: 400, color: '#333' }}>
                                                                {(() => {
                                                                    let parsedPhotoUrl = photoUrl;
                                                                    if (typeof photoUrl === 'string') {
                                                                        try {
                                                                            parsedPhotoUrl = JSON.parse(photoUrl);
                                                                        } catch (error) {
                                                                            return `Photo ${index + 1}`;
                                                                        }
                                                                    }
                                                                    return (typeof parsedPhotoUrl === 'object' && parsedPhotoUrl.name) ? parsedPhotoUrl.name : `Photo ${index + 1}`;
                                                                })()}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    let parsedPhotoUrl = photoUrl;
                                                                    if (typeof photoUrl === 'string') {
                                                                        try {
                                                                            parsedPhotoUrl = JSON.parse(photoUrl);
                                                                        } catch (error) {
                                                                            return;
                                                                        }
                                                                    }
                                                                    
                                                                    if (parsedPhotoUrl && typeof parsedPhotoUrl === 'object') {
                                                                        const { filePath, name, bucketName } = parsedPhotoUrl;
                                                                        viewFile(filePath, name, bucketName);
                                                                    }
                                                                }}
                                                                sx={{ 
                                                                    color: '#666',
                                                                    backgroundColor: '#fff',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: 0.5,
                                                                    width: 24,
                                                                    height: 24,
                                                                    '&:hover': { 
                                                                        backgroundColor: '#f0f0f0',
                                                                        borderColor: '#999'
                                                                    }
                                                                }}
                                                            >
                                                                <VisibilityIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    let parsedPhotoUrl = photoUrl;
                                                                    if (typeof photoUrl === 'string') {
                                                                        try {
                                                                            parsedPhotoUrl = JSON.parse(photoUrl);
                                                                        } catch (error) {
                                                                            return;
                                                                        }
                                                                    }
                                                                    
                                                                    if (parsedPhotoUrl && typeof parsedPhotoUrl === 'object') {
                                                                        const { filePath, name, bucketName } = parsedPhotoUrl;
                                                                        downloadFile(filePath, name, bucketName);
                                                                    }
                                                                }}
                                                                sx={{ 
                                                                    color: '#666',
                                                                    backgroundColor: '#fff',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: 0.5,
                                                                    width: 24,
                                                                    height: 24,
                                                                    '&:hover': { 
                                                                        backgroundColor: '#f0f0f0',
                                                                        borderColor: '#999'
                                                                    }
                                                                }}
                                                            >
                                                                <DownloadIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Grid>
                                        )}

                                        {/* Document Attachments */}
                                        {test.document_urls?.length > 0 && (
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: theme.palette.text.secondary }}>
                                                    Document Attachments ({test.document_urls.length})
                                                </Typography>
                                                {test.document_urls.map((docUrl, index) => (
                                                    <Box 
                                                        key={index}
                                                        sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'space-between',
                                                            p: 1,
                                                            mb: 0.5,
                                                            border: `1px solid ${theme.palette.grey[300]}`,
                                                            borderRadius: 2,
                                                            backgroundColor: alpha(theme.palette.grey[100], 0.5),
                                                            backdropFilter: 'blur(10px)'
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <FileIcon sx={{ color: '#666', fontSize: 16 }} />
                                                            <Typography variant="caption" sx={{ fontWeight: 400, color: '#333' }}>
                                                                {(() => {
                                                                    let parsedDocUrl = docUrl;
                                                                    if (typeof docUrl === 'string') {
                                                                        try {
                                                                            parsedDocUrl = JSON.parse(docUrl);
                                                                        } catch (error) {
                                                                            return `Document ${index + 1}`;
                                                                        }
                                                                    }
                                                                    return (typeof parsedDocUrl === 'object' && parsedDocUrl.name) ? parsedDocUrl.name : `Document ${index + 1}`;
                                                                })()}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    let parsedDocUrl = docUrl;
                                                                    if (typeof docUrl === 'string') {
                                                                        try {
                                                                            parsedDocUrl = JSON.parse(docUrl);
                                                                        } catch (error) {
                                                                            return;
                                                                        }
                                                                    }
                                                                    
                                                                    if (parsedDocUrl && typeof parsedDocUrl === 'object') {
                                                                        const { filePath, name, bucketName } = parsedDocUrl;
                                                                        viewFile(filePath, name, bucketName);
                                                                    }
                                                                }}
                                                                sx={{ 
                                                                    color: '#666',
                                                                    backgroundColor: '#fff',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: 0.5,
                                                                    width: 24,
                                                                    height: 24,
                                                                    '&:hover': { 
                                                                        backgroundColor: '#f0f0f0',
                                                                        borderColor: '#999'
                                                                    }
                                                                }}
                                                            >
                                                                <VisibilityIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    let parsedDocUrl = docUrl;
                                                                    if (typeof docUrl === 'string') {
                                                                        try {
                                                                            parsedDocUrl = JSON.parse(docUrl);
                                                                        } catch (error) {
                                                                            return;
                                                                        }
                                                                    }
                                                                    
                                                                    if (parsedDocUrl && typeof parsedDocUrl === 'object') {
                                                                        const { filePath, name, bucketName } = parsedDocUrl;
                                                                        downloadFile(filePath, name, bucketName);
                                                                    }
                                                                }}
                                                                sx={{ 
                                                                    color: '#666',
                                                                    backgroundColor: '#fff',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: 0.5,
                                                                    width: 24,
                                                                    height: 24,
                                                                    '&:hover': { 
                                                                        backgroundColor: '#f0f0f0',
                                                                        borderColor: '#999'
                                                                    }
                                                                }}
                                                            >
                                                                <DownloadIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            )}
                        </Box>
                    </TabPanel>
                </Box>
            </Collapse>
        </Card>
    );
} 